import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import mainStyle from "../../../mainStyle.module.scss";
import { Box, TextField, Typography } from "@mui/material";
import CollapseCard from "components/Collapse";
import CustomMenu from "components/Menu";
import ChipTag from 'components/ChipTag';
import { Loading } from "components/Loading";

import { useTheme } from "@mui/material/styles";
import { ReactComponent as ExpandIcon } from "assets/images/icons/btn_topology_expand.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_btn_bin.svg";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Helper
import {
  getPortTagsRequestParams,
  getPortTagsCreatePayload,
  getWiredPortTagsRequestParams,
  getWiredPortTagsUpdatePayload,
  getWiredPortTagsRemovePayload,
  initChips,
  initTags,
} from "./helper";

const GeneralDrawer = ({
  auth,
  activeRow,
  setActiveRow,
  setOriginalRowData,
  initCallback,
  showEditGeneral,
}) => {
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const uuids = useMemo(() => {
    return {
      portTags: {
        read: uuidv4(),
        create: uuidv4(),
      },
      chipTags: {
        update: uuidv4(),
        remove: uuidv4(),
      }
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [removeAnchorEl, setRemoveAnchorEl] = useState(null);
  const [addAnchorEl, setAddAnchorEl] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [portChips, setPortChips] = useState([]);
  const [portTags, setPortTags] = useState([]);
  const profileActiveRow = useMemo(() => activeRow?.selectedProfile ?? null, [activeRow?.selectedProfile]);
  const isProfileMode = useMemo(() => !activeRow?.selectedProfile?.isDefault, [activeRow?.selectedProfile?.isDefault]);

  const handleRemoveClick = (event, tag) => {
    setRemoveAnchorEl(event.currentTarget);
    setSelectedTag(tag);
  };

  const handleRemoveClose = () => {
    setRemoveAnchorEl(null);
    setSelectedTag(null);
  };

  const handleRemove = (tagToRemove) => {
    setActiveRow((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
    handleRemoveClose();
  };

  const handleAddClick = (event) => {
    setAddAnchorEl(event.currentTarget);
  };

  const handleAddClose = () => {
    setAddAnchorEl(null);
    setSearchValue("");
  };

  const handleAdd = (newTag) => {
    if (!newTag) return;
    setActiveRow((prev) => ({
      ...prev,
      tags: [...(prev.tags || []), newTag],
    }));
    handleAddClose();
  };

  const rowStyle = {
    display: "flex",
    gap: 3,
    alignItems: "center",
    width: "100%",
  };

  const readOnlyStyle = {
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[1],
  };

  const fetchPortTags = () => {
    setIsLoading(true);

    const { apiPaths, restfulParams } = getPortTagsRequestParams({ auth, method: "read" });

    sendRequest(
      apiPaths,
      uuids.portTags.read,
      restfulParams
    );
  }

  const createPortTags = newTag => {
    setIsLoading(true);

    const { apiPaths, restfulParams } = getPortTagsRequestParams({ auth, method: "create" });
    const payload = getPortTagsCreatePayload({ tags: newTag });

    sendRequest(
      apiPaths,
      uuids.portTags.create,
      restfulParams,
      payload
    );
  }

  const updateChips = newChip => {
    setIsLoading(true);

    const updateChips = [...portChips, newChip];
    const updateFlatChips = updateChips.map(chip => chip.tagId);
    const { apiPaths, restfulParams } = getWiredPortTagsRequestParams({ auth, method: "update" });
    const { payload } = getWiredPortTagsUpdatePayload({ activeRow, chipIds: updateFlatChips });

    sendRequest(
      apiPaths,
      uuids.chipTags.update,
      restfulParams,
      payload
    );

    setActiveRow({
      ...activeRow,
      portTags: updateFlatChips
    });

    setOriginalRowData({
      ...activeRow,
      portTags: updateFlatChips
    });

    setPortChips(updateChips);
  }

  const removeChips = newChip => {
    setIsLoading(true);

    const removedChips = portChips.filter(chip => chip.tagId !== newChip.tagId);
    const removedFlatChips = removedChips.map(chip => chip.tagId);
    const { apiPaths, restfulParams } = getWiredPortTagsRequestParams({ auth, method: "update" });
    const { payload } = getWiredPortTagsRemovePayload({ activeRow, chipIds: removedFlatChips });

    sendRequest(
      apiPaths,
      uuids.chipTags.remove,
      restfulParams,
      payload
    );

    setActiveRow({
      ...activeRow,
      portTags: removedFlatChips
    });

    setOriginalRowData({
      ...activeRow,
      portTags: removedFlatChips
    });

    setPortChips(removedChips);
  }

  useEffect(() => {
    fetchPortTags();
  }, []);

  useEffect(() => {
    if (!dataMap?.[uuids.portTags.read]) return;

    const portTagsResp = getData(uuids.portTags.read);
    const portTagsRespData = portTagsResp?.data ?? null;

    // Read tags
    if (portTagsRespData) {
      if (portTagsRespData.hasOwnProperty("code") && portTagsRespData.code !== 200) {
        // error handling
      } else {

        const chips = initChips({ chips: activeRow?.portTags, tags: portTagsRespData })
        setPortChips(chips);
        setPortTags(portTagsRespData);
      }
    }

    if(portTagsResp) setIsLoading(false);

  }, [dataMap?.[uuids.portTags.read]]);

  useEffect(() => {
    // Create tags
    if (!dataMap?.[uuids.portTags.create]) return;

    const creatingResponse = getData(uuids.portTags.create);
    if (creatingResponse) {
      fetchPortTags();
      setIsLoading(false);
    }

  }, [dataMap?.[uuids.portTags.create]]);

  useEffect(() => {
    // Update chips
    if (!dataMap?.[uuids.chipTags.update]) return;

    const updatingResponse = getData(uuids.chipTags.update);
    if (updatingResponse) {
      initCallback();
      setIsLoading(false);
    }
  }, [dataMap?.[uuids.chipTags.update]]);

  useEffect(() => {
    // Remove chips
    if (!dataMap?.[uuids.chipTags.remove]) return;

    const removingResponse = getData(uuids.chipTags.remove);
    if (removingResponse) {
      initCallback();
      setIsLoading(false);
    }
  }, [dataMap?.[uuids.chipTags.remove]]);

  return (
    <>
      { isLoading && <Loading/> }

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          px: 4,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <CollapseCard title="Port">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {activeRow && (
              <>
                {/* <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Tags
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {activeRow.tags && activeRow.tags.length > 0 ? (
                      activeRow.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            minWidth: "46px",
                            height: "25px",
                            padding: "0 8px",
                            backgroundColor: theme.palette.primary.subBlue,
                            color: theme.palette.primary.main,
                            fontSize: theme.typography.customFontSize[9],
                            borderRadius: theme.shape.customBorderRadius[0],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleRemoveClick(e, tag)}
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span>-</span>
                    )}
                  </Box>

                  <CustomMenu
                    anchorEl={removeAnchorEl}
                    open={Boolean(removeAnchorEl)}
                    handleMenuClose={handleRemoveClose}
                    maxWidth={112}
                    height={41}
                    mt={2}
                    ml={8}
                    top="-12px"
                    right="78px"
                    borderLeftWidth="8px"
                    borderRightWidth="8px"
                    borderBottomWidth="12px"
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        color: theme.palette.primary.main,
                      }}
                      onClick={() => handleRemove(selectedTag)}
                    >
                      <RemoveIcon />
                      Remove
                    </Box>
                  </CustomMenu>

                  <ExpandIcon
                    style={{ cursor: "pointer" }}
                    onClick={handleAddClick}
                  />

                  <CustomMenu
                    anchorEl={addAnchorEl}
                    handleMenuClose={handleAddClose}
                    maxWidth={216}
                    height={268}
                    mt={2}
                    ml={22}
                    top="-12px"
                    right="174px"
                    borderLeftWidth="8px"
                    z
                    borderBottomWidth="12px"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        py: 0.5,
                        borderBottom: `1px solid ${theme.palette.primary.mainHover}`,
                      }}
                    >
                      <TextField
                        variant="standard"
                        placeholder="Search..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        fullWidth
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: "14px" },
                        }}
                      />
                      <Box
                        onClick={() => handleAdd(searchValue)}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ExpandIcon />
                      </Box>
                    </Box>

                    {/* <List sx={{ maxHeight: 180, overflowY: "auto" }}>
                      {options
                        .filter((opt) =>
                          opt.toLowerCase().includes(searchValue.toLowerCase())
                        )
                        .map((opt, idx) => (
                          <ListItem
                            key={idx}
                            button
                            onClick={() => handleAdd(opt)}
                            sx={{
                              "&:hover": {
                                bgcolor: theme.palette.primary.lightBlue,
                              },
                            }}
                          >
                            <ListItemText primary={opt} />
                          </ListItem>
                        ))}
                    </List>
                  </CustomMenu>
                </Box> */}

                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Tags
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    {(!Array.isArray(portChips) || portChips.length === 0) && <Box sx={{ marginRight: 1 }}>-</Box>}
                    <ChipTag
                      isChipsEdit={false}
                      chips={portChips}
                      tags={portTags}
                      onChipsRemoveChange={props => {
                        const { removedChip } = props;
                        removeChips(removedChip)
                      }}
                      onChipsAddChange={props => {
                        const { newChip } = props;
                        updateChips(newChip);
                      }}
                      onTagsChange={props => {
                        const { newTag } = props;
                        createPortTags(newTag)
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Profile
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {activeRow.portProfileName || "-"}
                  </Typography>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    PVID
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    { isProfileMode && ( profileActiveRow?.PVID || "-" ) }
                    { !isProfileMode && ( activeRow.PVID || "-" ) }
                  </Typography>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Allow VLAN
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    { isProfileMode && ( profileActiveRow?.allowVlan || "-" ) }
                    { !isProfileMode && ( activeRow.allowVlan || "-" ) }
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CollapseCard>
        <CollapseCard title="Device">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {activeRow && (
              <>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Device Name
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {activeRow.deviceName || "-"}
                  </Typography>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    MAC Address
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {" "}
                    {activeRow.macAddress || "-"}
                  </Typography>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Model
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {activeRow.modelName || "-"}
                  </Typography>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Hardware
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {activeRow.hardwareVersion || "-"}
                  </Typography>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Firmware
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {activeRow.firmwareVersion || "-"}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CollapseCard>
      </Box>
    </>
  );
};
export default GeneralDrawer;
